/*
 * Email-this-plan widget for the results screen. Split into two pieces
 * because they live in different slots inside the "Take this plan with
 * you" card: the toggle button sits in the .pb-share-actions row next
 * to Print/Copy/Start-over, while the form panel appears below the row
 * (full width). State is owned by the parent (ResultsPage) so both
 * pieces can co-exist in different DOM positions.
 *
 * The markdown body is computed from the public diagnostic answers
 * (same logic the email pipeline uses on first submission) and posted
 * to POST /v1/submissions/{id}/email along with the recipient list.
 * The server caps recipients at 5 per request.
 */
import { useState } from 'react';
import { api } from '@shared/api/client';
import type { EmailPlanFailure } from '@shared/types/api';
import { usePlanBuilderConfigContext } from '../PlanBuilderConfigContext';

const MAX_RECIPIENTS = 5;
// Same lightweight shape check the API uses (PayloadValidationHelpers.RequiredEmail).
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ToggleProps {
  open: boolean;
  onToggle(): void;
}

export function EmailPlanToggle({ open, onToggle }: ToggleProps) {
  const config = usePlanBuilderConfigContext();
  return (
    <button
      type="button"
      className={`pb-email-toggle${open ? ' active' : ''}`}
      aria-expanded={open}
      aria-controls="pb-email-panel"
      onClick={onToggle}
    >
      {config.email.ctaLabel}
    </button>
  );
}

interface PanelProps {
  submissionId: string;
  buildMarkdown: () => string;
  onClose(): void;
}

interface SendResult {
  sent: string[];
  failed: EmailPlanFailure[];
}

export function EmailPlanForm({ submissionId, buildMarkdown, onClose }: PanelProps) {
  const config = usePlanBuilderConfigContext();
  const [emails, setEmails] = useState<string[]>(['']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SendResult | null>(null);

  const updateEmail = (i: number, v: string) =>
    setEmails((curr) => curr.map((e, idx) => (idx === i ? v : e)));

  const removeEmail = (i: number) =>
    setEmails((curr) => (curr.length === 1 ? [''] : curr.filter((_, idx) => idx !== i)));

  const addEmail = () =>
    setEmails((curr) => (curr.length >= MAX_RECIPIENTS ? curr : [...curr, '']));

  const submit = async () => {
    setError(null);
    setResult(null);

    const trimmed = emails.map((e) => e.trim()).filter((e) => e.length > 0);
    if (trimmed.length === 0) {
      setError('Add at least one email address.');
      return;
    }
    const invalid = trimmed.filter((e) => !EMAIL_SHAPE.test(e));
    if (invalid.length > 0) {
      setError(`Not a valid email: ${invalid.join(', ')}`);
      return;
    }
    const seen = new Set<string>();
    const unique: string[] = [];
    for (const e of trimmed) {
      const key = e.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(e);
      }
    }
    if (unique.length > MAX_RECIPIENTS) {
      setError(`Up to ${MAX_RECIPIENTS} recipients per send.`);
      return;
    }

    setBusy(true);
    try {
      const res = await api.emailPlan(submissionId, {
        recipients: unique,
        report_markdown: buildMarkdown(),
      });
      setResult({ sent: res.sent, failed: res.failed });
      if (res.failed.length === 0) {
        setEmails(['']);
      }
    } catch {
      setError("Couldn't send right now. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div id="pb-email-panel" className="pb-email-panel" aria-label="Email this plan">
      <div className="pb-email-panel-header">
        <div className="pb-email-panel-title">{config.email.panelTitle}</div>
        <button
          type="button"
          className="pb-email-panel-close"
          aria-label="Close email form"
          onClick={onClose}
        >
          ×
        </button>
      </div>
      <p className="pb-email-panel-sub">
        {config.email.panelBody}
      </p>

      <div className="pb-email-panel-fields">
        {emails.map((value, i) => (
          <div key={i} className="pb-email-panel-row">
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              className="pb-email-panel-input"
              placeholder="name@company.com"
              value={value}
              disabled={busy}
              onChange={(e) => updateEmail(i, e.target.value)}
            />
            {(emails.length > 1 || value.length > 0) && (
              <button
                type="button"
                className="pb-email-panel-remove"
                aria-label={`Remove recipient ${i + 1}`}
                disabled={busy}
                onClick={() => removeEmail(i)}
              >
                ×
              </button>
            )}
          </div>
        ))}
        {emails.length < MAX_RECIPIENTS && (
          <button
            type="button"
            className="pb-email-panel-add"
            disabled={busy}
            onClick={addEmail}
          >
            + Add another recipient
          </button>
        )}
      </div>

      {error && (
        <div className="pb-email-panel-error" role="alert">
          {error}
        </div>
      )}

      {result && (
        <div
          className={`pb-email-panel-result${result.failed.length === 0 ? ' ok' : ' partial'}`}
          role="status"
        >
          {result.sent.length > 0 && (
            <div>
              Sent to <strong>{result.sent.join(', ')}</strong>.
            </div>
          )}
          {result.failed.length > 0 && (
            <div>
              Couldn't send to{' '}
              <strong>{result.failed.map((f) => f.email).join(', ')}</strong>.
              Try again, or check the address.
            </div>
          )}
        </div>
      )}

      <div className="pb-email-panel-actions">
        <button
          type="button"
          className="pb-email-panel-send"
          onClick={submit}
          disabled={busy}
        >
          {busy ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
}
